import { useEffect, useState, useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import FileUploader from "../uploader/fileUploader";
import { downloadBoxFile } from "../utilities/downloadFiles";
import { fetchBoxAccessToken } from "../../store/common/authSlice";
import { useParams } from "react-router-dom";
// import { fetchCaseById } from "../../store/user/userSlice";
import { fetchCaseFilesByCaseId } from "../../store/common/fetchBoxFileSlice";
import {
  deleteBoxFiles,
  resetDeleteState,
} from "../../store/common/deleteBoxFileSlice";

import { useToast } from "../utilities/Toast";
import ConfirmModal from "../utilities/confirmModal";
import { fzPatientNamesFromDetails, isFzCaseId } from "../../utils/fzLab";

const ClientUploads = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { user, boxAccessToken } = useAppSelector((s) => s.auth);
  const { employee } = useAppSelector((s) => s.allocationAuth);
  const { case_id } = useParams<{ case_id: string }>();
  const { selectedCase, isLoading } = useAppSelector((s) => s.adminCases);
  const { caseFiles, fetching } = useAppSelector((s) => s.fileUpload);
  const {
    loading: deleting,
    success: deleteSuccess,
    error: deleteError,
  } = useAppSelector((s) => s.deleteBoxFiles);

  useEffect(() => {
    if (!case_id) return;
    dispatch(fetchBoxAccessToken());
    // dispatch(fetchCaseById(case_id));
    dispatch(fetchCaseFilesByCaseId({ case_id }));
  }, [case_id, dispatch]);

  const [downloadState, setDownloadState] = useState<
    Record<number, "downloading" | "error" | "done">
  >({});

  const handleDownload = useCallback(
    async (fileId: number, fileName: string) => {
      setDownloadState((s) => ({ ...s, [fileId]: "downloading" }));
      try {
        await downloadBoxFile(
          fileId.toString(),
          fileName,
          boxAccessToken || "",
        );
        setDownloadState((s) => ({ ...s, [fileId]: "done" }));
      } catch {
        setDownloadState((s) => ({ ...s, [fileId]: "error" }));
      }
    },
    [boxAccessToken],
  );

  const clientUploadedFiles = useMemo(
    () =>
      caseFiles.filter((f) => f.file_type?.toLowerCase() === "client_upload"),
    [caseFiles],
  );
  const fzCase = isFzCaseId(case_id);
  const fzNames = useMemo(
    () =>
      fzCase && selectedCase?.case_id === case_id
        ? fzPatientNamesFromDetails(selectedCase?.details_json)
        : [],
    [fzCase, case_id, selectedCase],
  );

  // --- Delete Mode ---
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<number[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const toggleFileSelect = (fileId: number) => {
    setSelectedForDelete((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId],
    );
  };

  const allSelected =
    clientUploadedFiles.length > 0 &&
    selectedForDelete.length === clientUploadedFiles.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedForDelete([]);
    } else {
      setSelectedForDelete(clientUploadedFiles.map((f) => f.fileId));
    }
  };

  const handleDeleteClick = () => {
    if (!selectedForDelete.length) return;
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowConfirmModal(false);
    await dispatch(
      deleteBoxFiles({
        fileIds: selectedForDelete.map(String),
        deleteBy:
          user?.role === "Super Admin"
            ? user?.user_name
            : employee?.emp_name || "",
      }),
    );
    setSelectedForDelete([]);
  };

  // Added success + error toast logic
  useEffect(() => {
    if (deleteSuccess && case_id) {
      dispatch(fetchCaseFilesByCaseId({ case_id }));
      dispatch(resetDeleteState());
      setDeleteMode(false);

      // Success toast
      showToast("File(s) deleted successfully!", "success");
    }
  }, [deleteSuccess, dispatch, case_id, showToast]);

  useEffect(() => {
    if (deleteError) {
      // Error toast
      showToast(`Failed to delete files: ${deleteError}`, "error");
      dispatch(resetDeleteState());
    }
  }, [deleteError, dispatch, showToast]);

  if ((isLoading || fetching) && (!fzCase || selectedCase?.case_id !== case_id))
    return <p className="text-gray-500 italic">Loading client uploads...</p>;

  return (
    <div className="w-full flex flex-col md:flex-row mx-auto p-4 gap-6">
      {/* Left Section */}
      <div className="w-full md:w-1/2 text-sm text-gray-800 space-y-2">
        {fzCase ? (
          <>
            <div className="text-md">
              Patient names : {fzNames.length}
            </div>
            {fzNames.length ? (
              <ul className="space-y-1">
                {fzNames.map((name) => (
                  <li key={name} className="break-all px-2 py-1 text-gray-800">
                    {name}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 italic">No patient names yet.</p>
            )}
          </>
        ) : null}
        {clientUploadedFiles.length > 0 ? (
          <div className="text-md">Total files : {clientUploadedFiles.length}</div>
        ) : null}
        {clientUploadedFiles && clientUploadedFiles.length > 0 ? (
          <div className="flex justify-between items-center mb-2 text-white">
            {user &&
              !["Admin Read", "Production", "Side Admin"].includes(user.role) && (
                <div className="space-x-2">
                  {!deleteMode ? (
                    <button
                      onClick={() => setDeleteMode(true)}
                      className="bg-red-500 px-4 py-1 rounded-lg hover:bg-red-600 cursor-pointer"
                    >
                      Delete Files
                    </button>
                  ) : (
                    <>
                      {/* CONFIRM DELETE */}
                      <button
                        onClick={handleDeleteClick}
                        disabled={deleting || selectedForDelete.length === 0}
                        className="bg-green-500 px-4 py-1 rounded-lg hover:bg-green-600 cursor-pointer"
                      >
                        Confirm Delete
                      </button>

                      {/* CANCEL */}
                      <button
                        onClick={() => {
                          setDeleteMode(false);
                          setSelectedForDelete([]);
                        }}
                        className="bg-gray-500 py-1 px-4 rounded-lg hover:bg-gray-600 cursor-pointer"
                      >
                        Cancel
                      </button>

                      {/* SELECT ALL */}
                      <label className="flex items-center gap-2 my-4 text-gray-900 mr-4">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                        />
                        Select All
                      </label>
                    </>
                  )}
                </div>
              )}
          </div>
        ) : null}

        {clientUploadedFiles.length ? (
          clientUploadedFiles.map((file) => {
            const status = downloadState[file.fileId];
            const isSelected = selectedForDelete.includes(file.fileId);
            return (
              <div
                key={file.fileId}
                className={`flex justify-between items-center px-2 rounded ${deleteMode && isSelected ? "bg-red-100" : ""
                  }`}
              >
                <div className="flex items-center gap-2">
                  {deleteMode && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleFileSelect(file.fileId)}
                    />
                  )}
                  <button
                    onClick={() =>
                      !deleteMode && handleDownload(file.fileId, file.file_name)
                    }
                    className={`${deleteMode
                        ? "text-gray-700"
                        : "text-[#1DA8C6] hover:underline"
                      }`}
                    disabled={status === "downloading" || deleteMode}
                  >
                    {file.file_name}
                  </button>
                </div>
                <div>
                  {status === "downloading" && (
                    <span className="text-gray-500 ml-2 italic">
                      Downloading...
                    </span>
                  )}
                  {status === "error" && (
                    <span className="text-red-500 ml-2 italic">Failed</span>
                  )}
                  {status === "done" && (
                    <span className="text-green-500 ml-2 italic">Done</span>
                  )}
                </div>
              </div>
            );
          })
        ) : fzCase ? null : (
          <p className="text-gray-500 italic">No client uploads yet.</p>
        )}
      </div>

      {/* Right Section */}
      <div className="w-full md:w-1/2 flex-1 space-y-6">
        {user &&
          !["Admin Read", "Production", "Side Admin"].includes(user.role) && (
            <>
              {isFzCaseId(case_id) ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                  FZ: selecting files only adds patient names from the file
                  names. Nothing is uploaded to Box.
                </div>
              ) : null}
              <FileUploader
                userId={user.user_id}
                caseId={case_id || ""}
                caseOwner={selectedCase?.case_owner || ""}
                caseFolderId={selectedCase?.box_folder_id?.toString() || ""}
                file_type="CLIENT_UPLOAD"
                accessToken={boxAccessToken || ""}
                dropTitle={
                  isFzCaseId(case_id)
                    ? "Drop client files to add patient names"
                    : undefined
                }
                dropHint={
                  isFzCaseId(case_id)
                    ? "Names only — no Box upload for FZ"
                    : undefined
                }
              />
            </>
          )}
      </div>

      {/* Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
        }}
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default ClientUploads;
